import React from "react";
import { Box, Button, Typography, AppBar, Toolbar, Paper } from "@mui/material";
import { styled } from "@mui/system";
import {
  BookOnlineOutlined,
  BusinessCenter,
  CastForEducationOutlined,
  FacebookOutlined,
  MailOutline,
  PersonOutline,
  Phone,
  ShareOutlined,
  Sos,
  SupervisorAccount,
  Translate,
  WhatsApp,
} from "@mui/icons-material";
import Work from "@mui/icons-material/Work";
import Group from "@mui/icons-material/Group";

// Styled components
const BannerContainer = styled(Box)({
  backgroundImage: `url(/images/back-main-image.jpg)`,
  backgroundSize: "cover",
  backgroundPosition: "center",
});

const NavButton = styled(Button)({
  color: "#000",
  fontWeight: 500,
  fontSize: "16px",
  textTransform: "none",
});

const CTAButton = styled(Button)({
  color: "#ffffff",
  fontWeight: 600,
  borderRadius: "20px",
  padding: "10px 20px",
  margin: "0 10px",
});

const AService = ({ icon, title, body }) => {
  return (
    <Paper
      sx={{
        padding: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: { xs: "80%", sm: "50%", md: 300 },
        "&:hover": {
          backgroundColor: `rgba(160, 82, 45, 0.4)`,
        },
      }}
    >
      {icon}
      <Typography mt={2} fontSize={16} fontWeight="bold" textAlign="center">
        {title}
      </Typography>
      <Typography textAlign="center">{body}</Typography>
    </Paper>
  );
};

const MainPage = () => {
  const handleScroll = (targetId) => {
    const section = document.getElementById(targetId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box>
      {/* Navbar */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: "background.default",
          boxShadow: "none",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "95%",
          }}
        >
          <Typography
            variant="h1"
            component="h1"
            sx={{ fontWeight: "bold", fontSize: 40, color: "#AF7817" }}
          >
            A C
          </Typography>
          <Box>
            <NavButton
              sx={{ mx: { xs: 0, sm: 2 } }}
              onClick={() => handleScroll("idAccueil")}
            >
              Accueil
            </NavButton>
            <NavButton
              sx={{ mx: { xs: 0, sm: 2 } }}
              onClick={() => handleScroll("idMyService")}
            >
              Services
            </NavButton>
            <NavButton
              sx={{ mx: { xs: 0, sm: 2 } }}
              onClick={() => handleScroll("idContact")}
            >
              Contact
            </NavButton>
          </Box>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <a href="https://wa.me/message/P2BMX25MDSSJM1" target="blanck">
              <Button variant="contained" color="success">
                Obtenir un accompagnement
              </Button>
            </a>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Banner */}
      <BannerContainer
        id="idAccueil"
        sx={{
          height: { xs: "60vh", md: "50vh" },
        }}
      >
        <Box
          sx={{
            backgroundColor: `rgba(160, 82, 45, 0.4)`,
            width: "100%",
            height: "100%",
            color: "#ffffff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "3.5rem" },
              lineHeight: 1.2,
            }}
          >
            Assurez la réussite de votre immigration au Canada !
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontSize: "1.2rem", marginTop: "20px", maxWidth: "600px" }}
          >
            Avec un accompagnement personnalisé pour maximiser vos chances de
            réussite aux tests TCF Canada, TEF et TCF Québec.
          </Typography>

          <Box sx={{ marginTop: "30px" }}>
            <a href="https://wa.me/message/P2BMX25MDSSJM1" target="blanck">
              <CTAButton variant="contained" color="success">
                Obtenir un accompagnement
              </CTAButton>
            </a>
          </Box>
        </Box>
      </BannerContainer>

      <Box sx={{ paddingX: 5 }}>
        <Typography
          id="idMyService"
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "2rem", md: "3.5rem" },
            lineHeight: 1.2,
            textAlign: "center",
            marginY: 5,
          }}
        >
          Nos Service
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "space-evenly",
            gap: 2,
          }}
        >
          <AService
            icon={<Translate color="primary" fontSize="large" />}
            title={"Consultation gratuite et inscription au test de langue"}
            body={
              "Profitez d'une consultation gratuite pour évaluer votre niveau et choisir le test de langue qui vous correspond le mieux (le TCF, TEF ou TCF Québec)."
            }
          />

          <AService
            icon={<Sos color="primary" fontSize="large" />}
            title={"Suivi quotidien et personnalisé"}
            body={
              "Nous vous guidons pas à pas, en ligne ou en présentiel, en tenant compte de votre emploi du temps et de vos besoins spécifiques. Ne restez plus seul face aux défis de la préparation."
            }
          />

          <AService
            icon={<CastForEducationOutlined color="primary" fontSize="large" />}
            title={"Préparation en ligne et en présentiel"}
            body={
              "Que vous préfériez suivre les sessions depuis chez vous ou en face à face, nous vous offrons un accompagnement adapté à votre emploi du temps. Bénéficiez de contenus interactifs"
            }
          />

          <AService
            icon={<BusinessCenter color="primary" fontSize="large" />}
            title={"Meilleur usage de vos packs"}
            body={
              "Nous vous aidons à exploiter pleinement chaque ressource, à comprendre les contenus essentiels et à appliquer les meilleures stratégies pour maximiser vos performances."
            }
          />

          <AService
            icon={<SupervisorAccount color="primary" fontSize="large" />}
            title={"Conseils pratiques et astuces"}
            body={
              "Que ce soit pour la gestion du stress, l'organisation de votre temps ou la maîtrise des contenus, obtenez les meilleures stratégies pour aborder vos examens avec confiance et sérénité."
            }
          />
        </Box>

        <Typography
          id="idContact"
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "2rem", md: "3.5rem" },
            lineHeight: 1.2,
            textAlign: "center",
            marginY: 5,
          }}
        >
          Contactez-nous
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "space-evenly",
            gap: 2,
            paddingBottom: 5,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              width: { xs: "80%", sm: "50%", md: 300 },
            }}
          >
            <img
              src="/images/customer-service.png"
              width="150"
              height="150"
              alt="Shop itemz Logo"
            />
          </Box>
          <Box
            sx={{
              textAlign: "center",
              width: { xs: "80%", sm: "50%", md: 300 },
            }}
          >
            <Typography>
              Ne laissez pas vos efforts être vains. Préparez-vous efficacement
              avec un accompagnement sur mesure !
            </Typography>

            <a href="tel:+237 652 761 855">
              <Button sx={{ marginY: 2 }} variant="text" startIcon={<Phone />}>
                +237 652 761 855
              </Button>
            </a>

            <a href="https://wa.me/message/P2BMX25MDSSJM1" target="blanck">
              <Button
                variant="contained"
                color="success"
                sx={{ textTransform: "none" }}
                startIcon={<WhatsApp />}
              >
                Contactez-nous maintenant
              </Button>
            </a>
          </Box>
        </Box>

        <Typography
          id="idContact"
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "2rem", md: "3.5rem" },
            lineHeight: 1.2,
            textAlign: "center",
            marginY: 5,
          }}
        >
          Témoignages
        </Typography>
        <Box
          sx={{
            textAlign: "center",
            marginBottom: 5,
          }}
        >
          <Typography mt={2} fontSize={16} fontWeight="bold" textAlign="center">
            Besoins d'avis de personnes ayant réussi leurs examens grâce à
            l'accompagnement de l'agence ? Ils nous ont fait confiance 👇👇👇.
          </Typography>
          <a
            href="https://chat.whatsapp.com/CUZKAWOizZxJWD2tpTdKDl"
            target="blanck"
          >
            <Button
              variant="contained"
              color="success"
              sx={{ textTransform: "none", marginTop: 4 }}
              startIcon={<WhatsApp />}
            >
              Groupe WhatsApp
            </Button>
          </a>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#153E7E",
          padding: { xs: 1, sm: 3 },
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            width: { xs: "80%", sm: "50%", md: 300 },
          }}
        >
          <Typography>Canada@2025 </Typography>
          <Typography>Consultation immigration</Typography>
        </Box>

        <Box
          sx={{
            textAlign: "center",
            width: { xs: "80%", sm: "50%", md: 300 },
          }}
        >
          <Typography>Formation au TCF Canada par @Mr NAMEkONG.</Typography>
          <Box
            direction="row"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <a href="https://wa.me/message/P2BMX25MDSSJM1" target="blanck">
              <Button
                variant="outlined"
                sx={{ color: "white", textTransform: "none" }}
                startIcon={<WhatsApp />}
              >
                Message
              </Button>
            </a>

            <a href="tel:+237 652 761 855">
              <Button
                sx={{ marginY: 2, color: "whitesmoke" }}
                variant="text"
                startIcon={<Phone />}
              >
                +237 652 761 855
              </Button>
            </a>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainPage;
